import { useState } from "react";
import { toast } from "react-toastify";
import { HiDocumentDownload, HiSearch, HiX } from "react-icons/hi";
import { Button, Modal, Spinner, TextInput } from "flowbite-react";
import usePdfPresencial from "../hooks/usePdfPresencial";

const ModalSeleccionPlantillas = ({ carpeta }: { carpeta: string }) => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [plantillas, setPlantillas] = useState<any[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState<any[]>([]);
  const { listarPlantillas, descargarPlantillas } = usePdfPresencial();

  const cerrarModal = () => {
    setModalVisible(false);
    setSelectedTemplates([]);
    setSelectAll(false);
    setSearchTerm("");
    setIsInputVisible(false);
  };

  const handleIconClick = () => {
    setIsInputVisible(!isInputVisible);
    setSearchTerm("");
  };

  const obtenerPlantillas = async (): Promise<void> => {
    setLoading(true);
    const plantillas = await listarPlantillas(carpeta);

    if (plantillas.length > 0) {
      setPlantillas(plantillas);
      setModalVisible(true);
    }
    setLoading(false);
  };

  const descargarPlantillasSeleccionadas = async () => {
    if (selectedTemplates.length === 0) {
      toast.warning("Debes seleccionar al menos una plantilla para descargar.");
      return;
    }
    await descargarPlantillas(carpeta, selectedTemplates);
    cerrarModal();
  };

  const toggleSeleccionPlantilla = (template: any) => {
    const updatedTemplates = selectedTemplates.includes(template)
      ? selectedTemplates.filter((t) => t !== template)
      : [...selectedTemplates, template];
    setSelectedTemplates(updatedTemplates);
    setSelectAll(updatedTemplates.length === plantillas.length);
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedTemplates([]);
    } else {
      setSelectedTemplates(plantillas);
    }
    setSelectAll(!selectAll);
  };

  return (
    <div>
      <button
        onClick={obtenerPlantillas}
        color="light"
        className="bg-green-500 text-white py-2 px-4 rounded flex items-center gap-2 hover:bg-green-600 transition"
      >
        Plantillas
      </button>

      <Modal
        show={modalVisible}
        onClose={cerrarModal}
        popup
        className="fixed inset-0 z-50 h-screen overflow-y-auto flex items-center justify-center bg-gray-900 bg-opacity-80 dark:bg-opacity-80"
      >
        <Modal.Body className="space-y-4">
          <HiDocumentDownload
            className={`mx-auto mb-2 h-12 w-12 text-green-500 animate-bounce mt-4`} // Agregando mt-4 para padding arriba
          />

          <h3 className="mb-3 text-[20px] font-semibold text-gray-600 dark:text-gray-300 border-b-2 border-green-500 text-center">
            Selecciona las plantillas que quieres descargar
          </h3>
          {loading ? (
            <div className="flex justify-center">
              <Spinner color="success" aria-label="Cargando plantillas..." />
            </div>
          ) : (
            <>
              <div className="flex items-center">
                <div
                  onClick={handleIconClick}
                  title={isInputVisible ? "Cerrar buscador" : "Buscar"}
                  className="cursor-pointer bg-white border-2 border-transparent rounded-full flex items-center justify-center mr-1 group"
                >
                  {isInputVisible ? (
                    <HiX
                      size={20}
                      className="text-gray-500 group-hover:text-gray-700 group-hover:scale-110 transition-all duration-200"
                    />
                  ) : (
                    <>
                      <HiSearch
                        size={20}
                        className="text-gray-500 group-hover:text-gray-700 group-hover:scale-110 transition-all duration-200"
                      />
                      <span className="ml-2 text-[15px] text-gray-500 font-semibold">
                        Buscar
                      </span>
                    </>
                  )}
                </div>

                {isInputVisible && (
                  <div className="relative flex-grow transition-all duration-300 ease-in-out">
                    <TextInput
                      id="search"
                      type="text"
                      placeholder="Buscar plantilla..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      icon={HiSearch}
                      required
                      autoComplete="off" // Desactiva el autocompletado
                      //className="pl-10"
                    />

                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm("")}
                        title="Borrar"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                      >
                        <HiX size={20} />
                      </button>
                    )}
                  </div>
                )}

                {/* Checkbox para seleccionar todas, visible solo cuando no hay texto */}
                {searchTerm.trim() === "" && (
                  <div className="flex items-center transition-all duration-300">
                    <input
                      type="checkbox"
                      id="selectAll"
                      checked={selectAll}
                      onChange={handleSelectAllChange}
                      className="cursor-pointer mr-2 h-5 w-5 text-green-500 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 checked:bg-green-500 checked:border-green-500 transition duration-200 ease-in-out transform hover:scale-105"
                      style={{ marginLeft: "9px" }}
                    />
                    <label
                      htmlFor="selectAll"
                      className="text-[15px] text-gray-500 font-semibold"
                    >
                      Seleccionar todas
                    </label>
                  </div>
                )}
              </div>

              {/* Contenedor con scroll */}
              <div
                className="space-y-3 max-h-60 overflow-y-auto"
                style={{
                  scrollbarWidth: "thin", // Para Firefox
                  scrollbarColor: "#8b8b8b #f1f1f1", // Para Firefox (thumb y track)
                  borderRadius: "8px", // Bordes redondeados para el contenedor
                  paddingRight: "10px", // Añadir un poco de espacio a la derecha para no solapar el scroll
                  border: "1px solid rgba(0, 0, 0, 0.2)", // Borde tenue
                }}
              >
                {plantillas
                  .filter(
                    (template) =>
                      template.toLowerCase().includes(searchTerm.toLowerCase()) // Filtra las plantillas por el término de búsqueda
                  )
                  .map((template, index) => (
                    <div
                      key={index}
                      className="flex items-center mb-3"
                      style={{ marginTop: index === 0 ? "10px" : "0" }} // Aplica el margen superior solo al primer elemento
                    >
                      <input
                        type="checkbox"
                        checked={selectedTemplates.includes(template)}
                        onChange={() => toggleSeleccionPlantilla(template)}
                        id={`template-${template}`}
                        className="cursor-pointer mr-3 h-5 w-5 text-green-500 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 checked:bg-green-500 checked:border-green-500 transition duration-200 ease-in-out transform hover:scale-105"
                        style={{ marginLeft: "8px" }}
                      />
                      <label
                        htmlFor={`template-${template}`}
                        className="text-[14px] text-gray-500 font-semibold text-gray-500"
                        style={{
                          wordBreak: "break-word", // Permite que el texto se divida en varias líneas si es necesario
                          whiteSpace: "normal", // Permite que el texto se ajuste dentro del contenedor
                        }}
                      >
                        {template}
                      </label>
                    </div>
                  ))}
              </div>
            </>
          )}

          {/* Botones de acción */}
          <div className="flex justify-center space-x-3 w-full">
            <Button
              onClick={cerrarModal}
              color="gray"
              className="bg-gray-500 hover:bg-gray-700 text-white font-semibold"
            >
              Cancelar
            </Button>
            <Button
              onClick={descargarPlantillasSeleccionadas}
              color="success"
              className="bg-green-500 hover:bg-green-700 text-white font-semibold"
              autoFocus
            >
              Descargar
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ModalSeleccionPlantillas;
